import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import resolveImage from "../../utils/imageUrl";
import {
  FiMenu,
  FiX,
  FiLogOut,
  FiChevronDown,
  FiBookOpen,
  FiHome,
  FiDroplet,
  FiTool,
  FiZap,
} from "react-icons/fi";
import "./Navbar.css";

const NAV_GROUPS = [
  {
    label: "Cleaning",
    icon: FiDroplet,
    items: [
      { label: "Vehicle Cleaning", slug: "vehicle-cleaning" },
      { label: "Household Cleaning", slug: "household-cleaning" },
    ],
  },
  {
    label: "Repairs",
    icon: FiTool,
    items: [
      { label: "Laptop Repair", slug: "laptop-repair" },
      { label: "Printer Repair", slug: "printer-repair" },
      { label: "Electrician", slug: "electrician" },
    ],
  },
  {
    label: "Home Services",
    icon: FiZap,
    items: [
      { label: "Dress Iron", slug: "dress-iron" },
      { label: "Hair Cutting", slug: "hair-cutting" },
    ],
  },
];

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null); // label of open dropdown
  const [mobileGroups, setMobileGroups] = useState({}); // {label: bool}
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target))
        setUserMenuOpen(false);
      if (navRef.current && !navRef.current.contains(e.target))
        setOpenGroup(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate("/");
  };

  const dashboardPath =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "technician"
        ? "/technician/dashboard"
        : "/dashboard";

  const currentCategory = new URLSearchParams(location.search).get("category");

  const isGroupActive = (group) =>
    group.items.some((item) => item.slug === currentCategory);

  const toggleGroup = (label) =>
    setOpenGroup((prev) => (prev === label ? null : label));

  const toggleMobileGroup = (label) =>
    setMobileGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  return (
    <nav
      className={`navbar ${scrolled ? "navbar-scrolled" : ""}`}
      ref={navRef}
      aria-label="Main navigation"
    >
      <div className="navbar-inner container">
        {/* Logo */}
        <Link
          to="/"
          className="navbar-logo"
          aria-label="Expert At Door — Home Services in Kolkata"
        >
          <img
            src="Expert At DoorLogo.png"
            alt="Expert At Door Logo"
            className="logo-image"
            style={{ width: "180px", height: "60px", borderRadius: "4px" }}
            width="180"
            height="60"
          />
        </Link>

        {/* Desktop nav */}
        <div className="navbar-links hide-mobile">
          <NavLink
            to="/services"
            end
            className={({ isActive }) =>
              "nav-link" + (isActive && !location.search ? " active" : "")
            }
          >
            All Services
          </NavLink>

          {NAV_GROUPS.map((group) => {
            const Icon = group.icon;
            const isOpen = openGroup === group.label;
            const isActive = isGroupActive(group);

            return (
              <div className="nav-dropdown-wrap" key={group.label}>
                <button
                  className={`nav-link nav-dropdown-trigger ${isActive ? "active" : ""} ${isOpen ? "open" : ""}`}
                  onClick={() => toggleGroup(group.label)}
                  aria-expanded={isOpen}
                >
                  <Icon size={13} />
                  {group.label}
                  <FiChevronDown
                    size={12}
                    className={`nav-chevron ${isOpen ? "open" : ""}`}
                  />
                </button>

                {isOpen && (
                  <div className="nav-dropdown">
                    <div className="nav-dropdown-header">{group.label}</div>
                    {group.items.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/services?category=${item.slug}`}
                        className={`nav-dropdown-item ${currentCategory === item.slug ? "active" : ""}`}
                        onClick={() => setOpenGroup(null)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right actions */}
        <div className="navbar-actions">
          {isAuthenticated ? (
            <div className="user-menu-wrap" ref={userMenuRef}>
              <button
                className="user-menu-trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-label="User menu"
              >
                <div className="user-avatar">
                  {user?.profileImage ? (
                    <img
                      src={resolveImage(user.profileImage)}
                      alt={user.name}
                    />
                  ) : (
                    <span>{user?.name?.[0]?.toUpperCase()}</span>
                  )}
                </div>
                <span className="user-name hide-mobile">
                  {user?.name?.split(" ")[0]}
                </span>
                <FiChevronDown
                  size={13}
                  className={`chevron ${userMenuOpen ? "open" : ""}`}
                />
              </button>

              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <p className="dropdown-name">{user?.name}</p>
                    <p className="dropdown-role">{user?.role}</p>
                  </div>
                  <div className="dropdown-divider" />
                  <Link
                    to={dashboardPath}
                    className="dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <FiHome size={14} /> Dashboard
                  </Link>
                  {user?.role === "customer" && (
                    <Link
                      to="/dashboard/bookings"
                      className="dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <FiBookOpen size={14} /> My Bookings
                    </Link>
                  )}
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item danger"
                    onClick={handleLogout}
                  >
                    <FiLogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost btn-sm hide-mobile">
                Sign In
              </Link>
              <Link to="/register" className="btn btn-primary btn-sm">
                Get Started
              </Link>
            </>
          )}

          <button
            className="hamburger hide-desktop"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX size={21} /> : <FiMenu size={21} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          <Link
            to="/services"
            className="mobile-link"
            onClick={() => setMobileOpen(false)}
          >
            All Services
          </Link>

          {NAV_GROUPS.map((group) => {
            const Icon = group.icon;
            const isOpen = !!mobileGroups[group.label];
            const isActive = isGroupActive(group);

            return (
              <div key={group.label} className="mobile-group">
                <button
                  className={`mobile-link mobile-group-trigger ${isActive ? "active" : ""}`}
                  onClick={() => toggleMobileGroup(group.label)}
                >
                  <span className="mobile-group-label">
                    <Icon size={14} />
                    {group.label}
                  </span>
                  <FiChevronDown
                    size={13}
                    className={`nav-chevron ${isOpen ? "open" : ""}`}
                  />
                </button>

                {isOpen && (
                  <div className="mobile-group-items">
                    {group.items.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/services?category=${item.slug}`}
                        className={`mobile-sub-link ${currentCategory === item.slug ? "active" : ""}`}
                        onClick={() => setMobileOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {isAuthenticated ? (
            <>
              <div className="mobile-divider" />
              <Link
                to={dashboardPath}
                className="mobile-link"
                onClick={() => setMobileOpen(false)}
              >
                Dashboard
              </Link>
              {user?.role === "customer" && (
                <Link
                  to="/dashboard/bookings"
                  className="mobile-link"
                  onClick={() => setMobileOpen(false)}
                >
                  My Bookings
                </Link>
              )}
              <button
                className="mobile-link mobile-link-danger"
                onClick={handleLogout}
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <div className="mobile-divider" />
              <Link
                to="/login"
                className="mobile-link"
                onClick={() => setMobileOpen(false)}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="mobile-link"
                onClick={() => setMobileOpen(false)}
              >
                Register
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
